import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import writeXlsxFile from 'write-excel-file';

import { timeToWarn } from './constants_comms';
import { CODE_COLOR, extraDays, maxItems } from './constants';

import translations from '../languages/i18n';
import { availableLanguages }  from '../languages/i18n';
import { getProgramLanguageContents } from  '../utils/comms';

// Carregam les regles de validació per a cada variable dels formularis
import * as validations from './varValidations';

// Afegeix hores minuts i segons a una data
export const addTimeToDate = (date) => {
  const time = Date.now(); // Torna els milisegons des de 01/01/1970 fins ara (GMT + 2)
  const now = new Date(time);
  date.setHours(now.getHours());
  date.setMinutes(now.getMinutes());
  date.setSeconds(now.getMinutes());
  return date;
};

// Passa els valors del formulari al format de resultats que necessita el servidor
export const buildResultSet = (values, userForm, userScheduleId) => {
  const now = moment().toDate();
  const results = [];
  // Ficam dins un array d'objectes, amb format { variable: identificador de la variable, value: valor }, els resultats del formulari
  // Al servidor s'han d'enviar les variables identificades amb el seu _id en lloc de pel seu sharedId
  Object.entries(values).forEach(([key, value]) => results.push({ variable: userForm.find((variable) => variable.sharedId === key)._id, value }));
  const resultSet = {
    userSchedule: userScheduleId,
    clientSideId: uuidv4(),
    time: now,
    // createdAt: no fa falta enviar-la, l'assigna el servidor
    // deletedAt: no fa falta enviar-la, l'assigna el servidor
    results,
  };
  return resultSet;
};

// Retorna la data en format DD-MM-YYYY a partir d'un Date
// Si se li passa days, els suma a la data que se li ha passat
export const convertToDDMMYYYY = (stringDate, days) => {
  const date = days ? moment(stringDate).add(days, 'days').toDate() : new Date(stringDate);
  return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
};

// Afegeix les validacions corresponents al sharedId de cada variable que s'ha de mostrar al formulari
export const createYupSchema = (previousValidation, currentValidation) => {
  const { sharedId } = currentValidation;
  // Si la validació no existeix, torna la validació que ha rebut
  if (!validations[sharedId]) {
    return previousValidation;
  }
  // Si la validació existeix, l'afegeix a les que ha rebut
  previousValidation[sharedId] = validations[sharedId];
  return previousValidation;
};

// Descarrega a l'ordinador un fitxer .json amb els textos d'un programa (en tots els idiomes disponibles)
export const downloadProgramTexts = async (sharedId, idProgram, token) => {
  let programTranslation =  {};
  try {
    // Obtenim els textos del programa des del servidor
    const programTranslationFromServer = await getProgramLanguageContents(idProgram, token);    
    if(programTranslationFromServer.status === 200 && programTranslationFromServer.data.status === 'ok') {
      programTranslation = programTranslationFromServer.data.languages;
    } else {
      throw 'internalCommonTranslationsNotAvailable';
    }
  } catch (error) {
    console.log(error);
  }

  // Convertim l'objecte amb els textos del programa en un fitxer .json i el descarregam a l'ordinador.
  // Exemple tret de: https://spin.atomicobject.com/2022/03/09/create-export-react-frontend/
  const fileData = JSON.stringify(programTranslation);
  const blob = new Blob([fileData], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = `${sharedId}_texts.json`;
  link.href = url;
  link.click();
};

export const computeOperation = (operationVariable, values) => {
  let result=0;
  switch (operationVariable.operation) {
    case 'SUM':            
      for (let variable of operationVariable.variables)
      {        
        const intValue = parseInt(values[variable])        
        if (intValue) result += intValue;  //sumam si té valor i és > 0          
      }
      break;    
    }        
    return result;      
}

// Funció per avaluar les regles de tipus simple dels inclusionCriteria
  export const evaluateSimpleRule = (variableValue, criteriaValue, criteriaVariable, operation, patientInfo ) => {
    let varValue = variableValue;
    
    // Comprovam si és un criteri que s'aplica a informació obtinguda del servidor
    if (!criteriaVariable.operation && criteriaVariable.startsWith('SERVERINFO_')) {
      switch (criteriaVariable) {
        case 'SERVERINFO_SEX':
          // Pos == en lloc de === per a que l'if doni true tant si patientInfo.sex és undefined com null. En principi si no disposam del sexe obtenim undefined
          if (patientInfo.sex == undefined || patientInfo.sex === 'O') return true; // Si no disposam del sexe del pacient o és ALTRES, fins haver decidit què fer, mostram la variable
          varValue = patientInfo.sex;
          break;
        case 'SERVERINFO_AGE':
          // Pos == en lloc de === per a que l'if doni true tant si patientInfo.birthdate és undefined com null. En principi, si no hi ha data, al servidor es guarda null
          if (patientInfo.birthdate == null) return true; // Si no disposam de l'edat del pacient, fins haver decidit què fer, mostram la variable
          varValue = getAge(patientInfo.birthdate);
          break;
      }          
    }  
  
    // ATENCIÓ: els valors inicials del formulari (els que es carreguen aquí loadFormikInitialValues()) no poden incloure null
    // per altra banda, hi ha criteris d'inclusió que comparen si el valor de formulari val null
    // Per solucionar el conflicte, comprov si el valor amb que s'ha de comparar és null i si és així el canvii per '' (que és el valor que tenen inicialment els valors del formulari)
    const normalizedCriteriaValue = criteriaValue === null ? '' : criteriaValue;
  
    let evaluationResult = false;
    // ATENCIÓ: normalment varValue és de tipus string i criteriaValue és de tipus numèric (o en algun cas null),
    // de moment no m'ha fet falta convertir les variables a un mateix tipus ja que la conversió que fa el propi javascript ha anat.
  
    // Si el valor de la variables és '' vol dir que encara no s'ha selecciona cap opció de resposta.
    // A les comparacions, javascript tracta '' com si fos 0, per evitar imprevistos (com que '' <=3 doni true) si la variable val '' no feim el switch
    if (varValue !== '') { 
      switch (operation) {
        case 'equals':
          // No faig una comparació estricta ja que no són del mateix tipus
          // evaluationResult = (varValue == criteriaValue);
          evaluationResult = Array.isArray(varValue) ? varValue.some((v) => v == normalizedCriteriaValue) : varValue == normalizedCriteriaValue;
          break;
        case 'greaterOrEqual':
          // No faig una comparació estricta ja que no són del mateix tipus
          // evaluationResult = (varValue == criteriaValue);
          evaluationResult = Array.isArray(varValue) ? varValue.some((v) => v >= normalizedCriteriaValue) : varValue >= normalizedCriteriaValue;
          break;
        case 'lowerOrEqual':
          // No faig una comparació estricta ja que no són del mateix tipus
          // evaluationResult = (varValue == criteriaValue);
          evaluationResult = Array.isArray(varValue) ? varValue.some((v) => v <= normalizedCriteriaValue) : varValue <= normalizedCriteriaValue;
          break;
      }
    }  
    // console.log('evaluationResult val: ', evaluationResult);
    return evaluationResult;
  };

// Funció per avaluar les regles de tipus complexa dels inclusionCriteria
export const evaluateComplexRule = (values, rule, patientInfo) => {  
  const fulfillsRules = rule.rules.map((r) => {
    let value=null;
    if (r.variable.operation) //el valor a comparar és un valor compost (suma de variables...)        
      value = computeOperation(r.variable, values);                                    
    else
      value = values[r.variable];
    return evaluateSimpleRule(value, r.value, r.variable, r.type, patientInfo);
  });
  let evaluationResult = false;
  switch (rule.type) {
    case 'and': // Dins fulfillsRules no hi pot haver cap false
      evaluationResult = !fulfillsRules.some((r) => r === false);
      break;
    case 'or': // Dins fulfillsRules al manco hi ha d'haver un true
      evaluationResult = fulfillsRules.some((r) => r === true);
      break;
  }
  return evaluationResult;
};

// Adaptam el contingut d'una llista d'eventTypes al format que adequat per poder representar-lo:
// - s'afegeix ' ,' a tots els eventTypes menys al darrer
// - només es mostra fins el nombre màxim d'eventTypes
// - si es supera s'afegix '...'
export const eventTypesList = (eventTypesArray, short) => {
  const eventList = eventTypesArray.map((event) => event.desc);
  if (eventList.length > maxItems && short) {
    const cutList = eventList.slice(0, maxItems);
    cutList.push('...');
    return cutList.join(', ');
  }
  return eventList.join(', ');
};

// Torna un array amb el valors d'un camp concret d'una llista
export const extractItems = (list, field) => {
  const itemsList = [];
  if (list) {
    switch (field) {
      case 'accessibleBy':
        // Tornam els que són administradors en majúscula, la resta en minúscula
        list.clinicians.map((data) => itemsList.push(data.admin ? data.clinician.username.toUpperCase() : data.clinician.username));
        break;
      case 'eventTypes':
        list.map((data) => itemsList.push(data.desc));
        break;
    }
    return itemsList;
  }
  return itemsList;
};

// Torna un array amb els ids dels programes que administra l'usuari
export const extractAdministeredProgramsId = (programs, user) => {
  const listOfPrograms = programs.filter((program) => {
    // Agafam els programes on el clínic és administrador
    return program.accessibleBy.clinicians.find((clinician) => clinician.clinician !== null && clinician.clinician._id === user && clinician.admin === true);
  });
  const listOfProgramsIds = listOfPrograms.map((program) => program._id);
  return listOfProgramsIds;
};


// Permet descarregar en format full de càlcul les dades del llistat
export const getDataSheet = async (data, fields) => {
  // Cream la fila de capçalera. Programs i enrolments tendran el mateix enxapçalament (programs)  
  const HEADER_ROW = fields.map((field) => {
    const header = field === 'enrolments' ? translations['_programs'] : translations[`_${field}`]; // Si la capçalera és 'enrolments' s'ha de mostrar com 'programs'    
    return ({value: header, fontWeight: 'bold'});
  });

  // Cream l'array que contendrà els objectes a carregar dins el full de càlcul, cada objecte és una fila
  let dataSheet = [
    HEADER_ROW,
  ];

  data.map((datarow) => {
    const row = fields.map((field) => {
      if (field === 'programs') {
        // Traiem els noms dels programes de l'array amb la informació dels programes
        const programs = datarow[field].map((program) => translations[program.sharedId]);
        // Convertim a una cadena l'array amb els noms dels programes
        return {type: String, value: programs.toString()};
      }
      if (field === 'enrolments') {
        const programs = datarow[field].map((enrol) => translations[enrol.program.sharedId]);
        return {type: String, value: programs.toString()};
      }
      if (field === 'birthdate') {
        return {type: String, value: datarow[field] && moment(datarow[field]).format('DD-MM-YYYY')};
      }
      if (field === 'sex') {
        const translatedSex = datarow[field] && (datarow[field] === 'M' ? translations['_man'] : datarow[field] === 'W' ? translations['_woman'] : translations['_otherSex']);
        return {type: String, value: translatedSex};
      }
      return {type: String, value: datarow[field]};
    });
    dataSheet.push(row);
  });

  await writeXlsxFile(dataSheet, {fileName: 'PROM_users_list.xlsx'});
}

// Cerca dins els camps (fields) de data la cadena searchString.
// Els camps s'indiquen com un array d'strings (['field1', 'field2', 'field3'])
/** MODIFICACIÓ FILTRATGE
Afegit cas particular per evaluació de field PROGRAMS tipo array.
Es necessari un cas concret ja que ha de decidir-se quin atribut es el que cercam per comparar.
*/
export const filteredSearch = (data, searchString, fields) => {
  // Normalitzam la cadena de text introduida al cercador
  const normalizedSearchString = searchString.normalize('NFD').replace(/[\u0300-\u036f]/g, "");
  const out = [];
  data.map((item) => {
    const outToShow = fields.map((f) => {
       // En cas de que dins data hi hagi la informació dels clinics, hem de cercar el programa dins l'array de programs
      if (f === 'programs') { // Clinicians
        const programField = item[f].map((progr) => {
          const translatedProgramName = translations[progr.sharedId]; // Cercam sobre els noms dels programes traduïts ja que des del servidor arriben només en anglès
          const normalizedFieldString = translatedProgramName && translatedProgramName.normalize('NFD').replace(/[\u0300-\u036f]/g, "");
          return (
            // En el cas d'un string, includes() mira si una cadena de text es troba dins una altra
            normalizedFieldString && normalizedFieldString.toLowerCase().includes(normalizedSearchString.toLowerCase())
          );
        });
        // En el cas d'un array, includes() mira si un array conté un element entre els seus valors
        return programField.includes(true);
      }
      // En cas de que dins data hi hagi la informació dels pacients, hem de cercar el programa dins l'array d'enrolments
      if (f === 'enrolments') {
        const programField = item[f].map((enrol) => {          
          const translatedProgramName = enrol.program && translations[enrol.program.sharedId]; // Cercam sobre els noms dels programes traduïts ja que des del servidor arriben només en anglès
          const normalizedFieldString = translatedProgramName && translatedProgramName.normalize('NFD').replace(/[\u0300-\u036f]/g, "");
          return (
            normalizedFieldString && normalizedFieldString.toLowerCase().includes(normalizedSearchString.toLowerCase())
          );
        });
        return programField.includes(true);
      }
      const normalizedFieldString = item[f] && item[f].normalize('NFD').replace(/[\u0300-\u036f]/g, '');      
      return (
        normalizedFieldString
          ? normalizedFieldString.toLowerCase().includes(normalizedSearchString.toLowerCase())
          : false
      );
    });
    if (outToShow.includes(true)) {
      out.push(item);
    }
  });

  return out;
};

// Genera un color hexadecimal aleatòri. Utilitzat a les gràfiques
export const generateRandomColor = () => {
  const n = (Math.random() * 0xfffff * 1000000).toString(16);
  return '#' + n.slice(0, 6);
}


// Retorna l'edat en anys del pacient
export const getAge = (dateString) => {
  const today = new Date();    
  const birthDate = new Date(dateString);
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
}

// Retorna el color de l'<Avatar /> emprat per indicar l'estat d'un formulari
export const getColors = (NmasUno) => {
  const index = NmasUno - 1;
  // En cas de que index sigui igual o superior al numero de posicions definides a CODE_COLOR, li assignam el darrer valor
  return index >= CODE_COLOR.length ? CODE_COLOR[CODE_COLOR.length - 1] : CODE_COLOR[index];
};

// Extreu les etiquetes de les dades d'un indicador.Utilitzat a les gràfiques
export const getIndicatorsLabels = (data) => {
  const labelsFromData = data.map((item) => Object.getOwnPropertyNames(item));
  // Ho passama a un array de 1 dimensió
  const flatLabels = labelsFromData.flatMap((label) => label);
  // Eliminam duplicats
  const uniqueLabelsWitDate = [...new Set(flatLabels)];        
  // Llevam l'etiqueta date
  const uniqueLabels = uniqueLabelsWitDate.filter((label)=> label!=='date');
  return uniqueLabels;
}

// Cerca dins els camps (fields) de data la cadena searchString.
// Els camps s'indiquen com un array d'strings (['field1', 'field2', 'field3'])
export const oldFilteredSearch = (data, searchString, fields) => {
  // Llevam els accents (`´) de la cadena que cercam
  const normalizedSearchString = searchString.normalize('NFD').replace(/[\u0300-\u036f]/g, "");
  return data.filter((d) => {
    return fields.some((f) => {
      // Llevam els accents (`´) del contingut del camp on cercam
      const normalizedString = d[f] && d[f].normalize('NFD').replace(/[\u0300-\u036f]/g, "");
      // Passam tant el contingut del camp com la cadena a minúscules
      return normalizedString && normalizedString.toLowerCase().includes(normalizedSearchString.toLowerCase());
    });
  });
};

// Funcions relacionades amb l'ordenació de llistats segons el valor d'una columna
// Ordena de manera estable, en cas de que a dos elements els hi toqui el mateix lloc a l'array ordenat es col.loquen segons l'ordre que tenien a l'array d'entrada
export const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((first, second) => {
    const comparisonResult = comparator(first[0], second[0]);
    if (comparisonResult !== 0) return comparisonResult;
    return first[1] - second[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

// Aplica les funcions d'ordenació
export const descendingComparator = (first, second, ordBy) => {
  if (ordBy === 'from' || ordBy === 'to' || ordBy === 'adaptedFrom' || ordBy === 'adaptedTo' || ordBy === 'createdAt') { // Ordenam per data d'apertura, tancament o creació del formulari
    if (moment(second[ordBy]).isBefore(moment(first[ordBy]))) return -1;
    if (moment(second[ordBy]).isAfter(moment(first[ordBy]))) return 1;
  } else if (ordBy === 'scheduleName') { // Ordenam pel text de la traducció no pel codi de l'scheduleName
    if (translations[second[ordBy]] < translations[first[ordBy]]) return -1;
    if (translations[second[ordBy]] > translations[first[ordBy]]) return 1;
  } else {
    if (second[ordBy] < first[ordBy]) return -1;
    if (second[ordBy] > first[ordBy]) return 1;
  }
  return 0;
};

// El - de davant el nom de la funció canvia el signe del que retorna la funció
export const getComparator = (ord, ordBy) => {
  return ord === 'desc'
    ? (first, second) => descendingComparator(first, second, ordBy)
    : (first, second) => -descendingComparator(first, second, ordBy);
};

// Calcula els dies que falten per a que caduqui un formulari. Torna:
// * un numero positiu superior a 0 si el formulari està obert (es pot editar)
// * 0 si el formulari encara no està obert (no ha caducat)
// * -1 si el formulari ha caducat
export const remainingDays = (from, daysToFillIn) => {
  const today = moment().format('YYYY-MM-DD');
  const firstDay = moment(from).format('YYYY-MM-DD');
  const lastDay = moment(from).add(daysToFillIn + extraDays, 'days').format('YYYY-MM-DD'); // Afegim extraDays (normalment 1) al daysToFillIn perquè començam a comptar els dies a partir del dia següent al from
  if (moment(today).isSameOrAfter(firstDay) && moment(today).isBefore(lastDay)) return moment(lastDay).diff(moment(today), 'days'); // El formulari està obert o es pot editar. ATENCIÓ: la segona condició és isBefore i no isSameOrBefore ja que lastDay ja inclou el dia "extra"
  if (moment(today).isBefore(firstDay)) return 0; // El formulari encara no s'ha obert
  return -1; // El formulari està caducat
};

// Elimina els objectes duplicats d'un array ("Aplana") segons la clau (propietat de l'objecte que es compara) proporcionada
// La manera de cridar aquesta funció és, per exemple,: uniqueByKey(objectsArray, (object) => object.key);
export const uniqueByKey = (data, key) => {
  return [
    ...new Map(
      data.map((x) => [key(x), x]),
    ).values(),
  ];
};

// Adaptam el contingut d'una llista d'usuaris al format adequat per poder representar-lo:
// - l'usuari administrador es mostra en majuscula
// - s'afegeix ' ,' a tots els usuaris menys al darrer
// - només es mostra fins el nombre màxim d'usuaris
// - si es supera s'afegix '...'
export const usersWithAccessList = (usersArray, short) => {
  const usersListArray = usersArray.map((user) => {
    if (user.clinician !== null && user.clinician.active === true) { // Només mostram els usuaris actius
      if (user.admin === true) {
        return user.clinician.username.toUpperCase();
      }
      return user.clinician.username;
    }
  });
  // Llevam els elements undefined (es corresponen als usuaris que no estan actius) de l'array
  const usersList = usersListArray.filter((username) => username !== undefined);
  if (usersList.length > maxItems && short) {
    const cutList = usersList.slice(0, maxItems);
    cutList.push('...');
    return cutList.join(', ');
  }
  return usersList.join(', ');
};

// Per comprovar que el token no ha caducat
export async function validToken(exp) {
  // Miram si falten menys de timeToWarn minuts per a que caduqui el token, si es aixi hem de tornar fer login
  if (exp * 1000 - Date.now() > timeToWarn * 60 * 1000) return true;
  return false;
  // PROVES
  // return 'yJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1c2VyIjoiaW5zcGkiLCJyb2xlIjoiUGF0aWVudCIsIl9pZCI6IjVkNjUxMWRmYzE0M2RhNDUxND';
  // PROVES
  // if (tokenInfo && tokenInfo.exp * 1000 - (new Date()).getTime() > 30 * 1000) {
  //  console.log('eoeoo');
  //  return tokenInfo.token;
  // }
}
